import React, { useState } from 'react';
import { notification, Justify } from 'tea-component';
import { useMemoizedFn } from 'ahooks';
import classnames from 'classnames';
import { cloneDeep, find, get, isEmpty } from 'lodash-es';
import SimilarModal from './components/SimilarModal';
import SimilarList from './components/SimilarList';
import Loading from '@gp/components/Loading';
import { reportBtnOperationLog } from '@gp/utils/common';
import './index.scss';
import CardSlot from '@/pages/globalpedia/components/CardSlot';
import HistoryBubble from '@gp/pages/Pipeline/ProjectDetails/components/HistoryBubble';
import { useEditProjectDetailInfo } from '@gp/pages/Pipeline/ProjectDetails/GameDetail/components/TeamGameInformation/hooks/useInformation';

// 临时数据 begin
const segmentOptions = [
  { text: 'Pitch', milestone: 4, value: 'Pitch' },
  { text: 'Concept', milestone: 1, value: 'Concept' },
  { text: 'First Playable', milestone: 7, value: 'First Playable' },
  {
    text: 'End of Pre-Production (Vertical Slice)',
    milestone: 2,
    value: 'End of Pre-Production (Vertical Slice)',
  },
  {
    text: 'Feature Complete (Alpha)',
    milestone: 3,
    value: 'Feature Complete (Alpha)',
  },
  {
    text: 'Content Complete (Beta)',
    milestone: 5,
    value: 'Content Complete (Beta)',
  },
  { text: 'Launch', milestone: 6, value: 'Launch' },
];

// 针对每一项对象，进行遍历，组转一维数组
const oneDimensionalArray = (list: any) => {
  const cloneDeepList = cloneDeep(list);
  return Object.keys(cloneDeepList).reduce((acc, milestoneKey) => {
    const array = cloneDeepList[milestoneKey];
    if (array.length > 0) {
      // 对每个非空数组，映射其元素为新的对象，并包含milestone和name属性
      const mappedItems = array.map((item: any) => ({
        ...item,
        milestone: get(
          find(segmentOptions, { value: milestoneKey }),
          'milestone',
          4,
        ),
        name: item.name, // 使用当前元素的name属性
        // 如果需要其他属性，也可以在这里添加
        resource_url: item?.resource_url,
        resource_id: item?.resource_id,
        resource_type: item?.resource_type,
        cloud_path: item?.cloud_path || item?.status,
        resource_cover: item?.resource_cover,
        file_size: item?.file_size,
        cover_list: item?.cover_list,
        error: item?.error,
      }));
      // 将映射后的数组添加到累加器中
      return acc.concat(mappedItems);
    }
    // 如果数组为空，则返回累加器不变
    return acc;
  }, []); // 初始累加器为一个空数组
};

// 需求如下:后端为要求性能存储原因，需要让前端，针对上传的文件，只上传删除过的以及重命名过的，其他没有变化的，不需要传递
function calculateDiffSet(ArrayFormKey: any[], cloneDeepCoverList: any[]) {
  // 首先处理cloneDeepCoverList中的元素
  const result = cloneDeepCoverList
    .map((item) => {
      // 查找ArrayFormKey中是否有相同resource_id的元素
      const existingItem = ArrayFormKey.find(
        (x) => x.resource_id === item.resource_id,
      );
      // 如果有，并且name未改变，直接返回null。如果有但名字或者封面改变，或者没有找到，返回新item，标记为is_del: 0
      return existingItem &&
        item?.name === existingItem?.name &&
        item?.resource_cover === existingItem?.resource_cover
        ? null
        : { ...item, is_del: 0 };
    })
    .filter(Boolean); // filter(Boolean)用于删除null的元素

  // 然后处理ArrayFormKey中的元素，检查是否存在于cloneDeepCoverList中
  ArrayFormKey.forEach((item) => {
    const existingItem = cloneDeepCoverList.find(
      (x) => x.resource_id === item.resource_id,
    );

    // 如果没有找到，说明应被标记为删除，向结果列表中添加新条目，并标记为is_del: 1
    if (!existingItem) {
      result.push({ ...item, is_del: 1 });
    }
  });

  return result;
}

export default function SimilarGames(props: any) {
  const {
    loading,
    gameInformationData,
    // 是否有编辑权限
    userIsAbleEdit,
    refreshFun,
    similarData,
    allCountryFlag,
    iconList,
  } = props;

  const { runAsync } = useEditProjectDetailInfo('VisualEdit');

  const [visible, setVisible] = useState(false);

  const close = useMemoizedFn(() => {
    setVisible(false);
  });

  const handleConfirm = useMemoizedFn(async (value) => {
    try {
      const { project_id: projectId, studio_entity_id: studioId } =
        gameInformationData;
      // 不允许上传错误类型的文件,最新文件
      // const list = oneDimensionalArray(segmentValueList).filter(
      //   (task) => !task?.error?.fileTypeError && !task?.error?.fileSizeError,
      // );
      const list: any = [];
      // 初始文件
      const initializeList = oneDimensionalArray(similarData?.list);
      // 过滤后的文件，包含文件名改动，文件封面改动，统统查找出来，返回给后端同学
      const getDiffSetParams = calculateDiffSet(initializeList, list);
      // 如果发现没有改动，就不请求这次接口
      if (isEmpty(getDiffSetParams)) {
        setVisible(false);
        return;
      }
      // 拿到入参，进行请求，关闭单框，出现成功提示
      const params = {
        project_id: projectId,
        studio_id: studioId,
        list: getDiffSetParams,
      };

      await runAsync(params);
      refreshFun?.();
      notification.success({
        description: 'Save success',
        duration: 1000,
      });
      close();
    } catch (error) {
      notification.error({
        description: 'Save failed. Please check the network',
        duration: 1000,
      });
    } finally {
      reportBtnOperationLog(
        'DETAIL_PROJECT_DETAIL_GAME_INFORMATION_EDIT_SUBMIT',
      );
    }
  });

  return (
    <div className="dbg-project-detail-visual-showcase">
      <CardSlot
        className={classnames('dbg-project-detail-visual-showcase-content')}
        title={
          <div className="dbg-project-detail-visual-showcase-head-title dbg-project-overview-head-title">
            Similar Games
            <HistoryBubble
              loading={loading}
              newTime={loading ? '-' : similarData?.update_time}
            />
          </div>
        }
        iconList={iconList}
        getChange={(value: any) => {
          if (value === 'pencil-m') {
            setVisible(!visible);
          }
        }}
      >
        {/* <Justify
          className="dbg-project-detail-visual-showcase-head"
          left={
            <div className="dbg-project-detail-visual-showcase-head-title dbg-project-overview-head-title">
              Similar Games
              <HistoryBubble loading={loading} newTime={loading ? '-' : similarData?.update_time} />
            </div>
          }
          right={
            userIsAbleEdit ? (
              <div className="dbg-project-detail-visual-showcase-head-options">
                <i
                  key={1}
                  className="dbg-card-slot-text"
                  onClick={() => {
                    setVisible(!visible);
                  }}
                >
                  {'Edit'}
                </i>
              </div>
            ) : null
          }
        /> */}

        {loading ? (
          <Loading loading style={{ height: 110, padding: 10 }} />
        ) : (
          <SimilarList
            userIsAbleEdit={userIsAbleEdit}
            similarData={similarData}
            allCountryFlag={allCountryFlag}
            getAdd={() => {
              setVisible(!visible);
            }}
          />
        )}
      </CardSlot>
      {/* Similar Games 编辑弹窗 */}
      <SimilarModal
        allCountryFlag={allCountryFlag}
        similarData={similarData}
        visible={visible}
        gameInformationData={gameInformationData}
        similarDataRefresh={refreshFun}
        close={() => {
          setVisible(!visible);
        }}
      />
    </div>
  );
}
